import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { scanQRCode } from '../actions/userActions';
import { toast } from 'react-toastify';
import { Scanner } from '@yudiel/react-qr-scanner'; // Change this line
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:9000/api';

const normalizeClubs = (clubs = []) => {
  const seen = new Set();
  const normalized = [];

  clubs.forEach((club) => {
    if (!club) return;
    const rawId = club._id || club.id;
    if (!rawId) return;
    const id = String(rawId);
    if (seen.has(id)) return;
    seen.add(id);

    normalized.push({
      _id: id,
      name: club.name || club.clubCode || 'Unnamed Club',
      clubCode: club.clubCode || '',
      locationName: club.location?.name || '',
    });
  });

  return normalized;
};

const QRScannerPage = () => {
  const [scanMode, setScanMode] = useState('camera');
  const [scanInput, setScanInput] = useState('');
  const [scannedData, setScannedData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showCamera, setShowCamera] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [accessibleClubs, setAccessibleClubs] = useState([]);
  const [selectedClubId, setSelectedClubId] = useState('');
  const [clubsLoading, setClubsLoading] = useState(false);
  const [clubLoadError, setClubLoadError] = useState('');
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const adminLogin = useSelector((state) => state.adminLogin || {});
  const { userInfo: adminInfo } = adminLogin;
  const getRoleInfo = useSelector((state) => state.getRoleInfo || {});
  const { getRoleData } = getRoleInfo;
  const effectiveRole = getRoleData || adminInfo?.role || {};
  const isSuperAdmin = effectiveRole?.isSuperAdmin === true;

  // Mobile detection on component mount and window resize
  useEffect(() => {
    const checkMobileDevice = () => {
      const screenWidth = window.innerWidth;
      const userAgent = navigator.userAgent.toLowerCase();

      // Check screen width (mobile if <= 768px)
      const isScreenMobile = screenWidth <= 768;

      // Check user agent for mobile devices
      const isMobileAgent = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini|mobile|tablet/i.test(userAgent);

      // Device is mobile if either condition is true
      setIsMobile(isScreenMobile || isMobileAgent);
    };

    checkMobileDevice();

    // Add event listener for window resize
    window.addEventListener('resize', checkMobileDevice);

    // Cleanup
    return () => window.removeEventListener('resize', checkMobileDevice);
  }, []);

  useEffect(() => {
    const token = adminInfo?.token;
    const assignedClubs = normalizeClubs(adminInfo?.assignedClubs || []);

    const applyClubs = (clubs) => {
      setAccessibleClubs(clubs);
      setSelectedClubId((prevSelected) => {
        if (prevSelected && clubs.some((club) => club._id === prevSelected)) {
          return prevSelected;
        }
        if (clubs.length === 1) {
          return clubs[0]._id;
        }
        return '';
      });
    };

    if (!token) {
      applyClubs([]);
      setClubLoadError('Login required to scan QR codes with club context.');
      setClubsLoading(false);
      return;
    }

    const loadAccessibleClubs = async () => {
      setClubsLoading(true);
      setClubLoadError('');

      if (!isSuperAdmin) {
        applyClubs(assignedClubs);
        if (!assignedClubs.length) {
          setClubLoadError('No clubs are assigned to your account. Contact admin.');
        }
        setClubsLoading(false);
        return;
      }

      try {
        const config = {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: {
            isActive: true,
            page: 1,
            limit: 500,
          },
        };

        const { data } = await axios.get(`${API_BASE}/clubs`, config);
        const fetchedClubs = normalizeClubs(data?.data || []);
        applyClubs(fetchedClubs);

        if (!fetchedClubs.length) {
          setClubLoadError('No active clubs available for scanning.');
        }
      } catch (error) {
        applyClubs(assignedClubs);
        if (assignedClubs.length) {
          setClubLoadError('Could not refresh active clubs. Using assigned clubs.');
        } else {
          setClubLoadError(
            error.response?.data?.message || 'Failed to load clubs for scanning.'
          );
        }
      } finally {
        setClubsLoading(false);
      }
    };

    loadAccessibleClubs();
  }, [adminInfo?.token, adminInfo?.assignedClubs, isSuperAdmin]);

  const handleScan = async (qrData = scanInput, method = null) => {
    if (!selectedClubId) {
      toast.error('Please select a club before scanning');
      return;
    }

    if (!qrData?.trim()) {
      toast.error('Please scan or enter QR code data');
      return;
    }

    try {
      setLoading(true);
      // Determine scan method: use provided method, or infer from scanMode state
      const scanMethodToUse = method || (scanMode === 'manual' ? 'MANUAL' : 'CAMERA');
      const data = await dispatch(scanQRCode(qrData, scanMethodToUse, selectedClubId));

      // Navigate to QRResultPage with the scanned data
      const encodedData = encodeURIComponent(JSON.stringify({
        ...data.userData,
        scanType: data.scanType || 'CHECK_IN',
        timestamp: new Date().getTime() // Add timestamp for expiration check
      }));

      toast.success(data.scanType === 'CHECK_OUT' ? 'Check-out successful!' : 'QR Code scanned successfully!');

      // Navigate to result page
      navigate(`/qr-result?data=${encodedData}`);
    } catch (error) {
      toast.error(error);
      setScannedData(null);
      setShowCamera(false);
    } finally {
      setLoading(false);
    }
  };

  const handleCameraScan = (result) => {
    if (result && result.length > 0) {
      const scannedText = result[0].rawValue;
      console.log('Camera scanned data:', scannedText);
      setScanInput(scannedText);
      handleScan(scannedText);
    }
  };

  const handleCameraError = (err) => {
    console.error('Camera error:', err);
    toast.error('Camera access failed. Please check permissions.');
    setShowCamera(false);
  };

  const resetScan = () => {
    setScannedData(null);
    setScanInput('');
    setShowCamera(true);
  };

  const canScan = Boolean(selectedClubId) && accessibleClubs.length > 0 && !clubsLoading;

  // If not mobile, show access denied screen
  if (!isMobile) {
    return (
      <div className="min-h-screen bg-gray-100">
        {/* Header */}
        <div className="bg-cm-gold text-cm-gold-ink p-4">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate(-1)}
              className="text-white hover:text-gray-200"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </button>
            <h1 className="text-xl font-bold">QR Scanner</h1>
          </div>
        </div>

        {/* Mobile Only Access Message */}
        <div className="flex items-center justify-center p-8" style={{ minHeight: 'calc(100vh - 72px)' }}>
          <div className="bg-white rounded-lg shadow-lg p-8 max-w-md text-center">
            <div className="mb-6">
              <svg className="w-24 h-24 mx-auto text-cm-gold-text" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Mobile Device Required</h2>
            <p className="text-gray-600 mb-6">
              The QR Scanner feature is only accessible on mobile devices for optimal camera functionality and user experience.
            </p>
            <div className="bg-cm-gold-soft border border-cm-border rounded-lg p-4 mb-6">
              <p className="text-sm text-cm-text">
                <strong>To access this feature:</strong>
              </p>
              <ul className="text-sm text-cm-gold-text mt-2 space-y-1 text-left list-disc list-inside">
                <li>Open this page on your mobile phone or tablet</li>
                <li>Use a device with screen width ≤ 768px</li>
                <li>Ensure your device has camera access enabled</li>
              </ul>
            </div>
            <button
              onClick={() => navigate(-1)}
              className="w-full bg-cm-gold text-cm-gold-ink py-3 px-6 rounded-lg hover:bg-cm-gold-hover transition-colors font-medium"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-cm-gold text-cm-gold-ink p-4">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate(-1)}
            className="text-white hover:text-gray-200"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </button>
          <h1 className="text-xl font-bold">QR Scanner</h1>
        </div>
      </div>

      <div className="p-4">
        <div className="bg-white rounded-lg p-4 mb-4 shadow-sm">
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Select Club for Check-In
          </label>
          <select
            value={selectedClubId}
            onChange={(e) => setSelectedClubId(e.target.value)}
            disabled={clubsLoading || accessibleClubs.length === 0}
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cm-gold disabled:bg-gray-100 disabled:text-gray-500"
          >
            <option value="">Select a club</option>
            {accessibleClubs.map((club) => (
              <option key={club._id} value={club._id}>
                {club.clubCode ? `${club.clubCode} - ${club.name}` : club.name}
              </option>
            ))}
          </select>
          {clubsLoading && (
            <p className="text-xs text-gray-500 mt-2">Loading clubs...</p>
          )}
          {!clubsLoading && clubLoadError && (
            <p className="text-xs text-red-600 mt-2">{clubLoadError}</p>
          )}
          {!clubsLoading && !clubLoadError && selectedClubId && (
            <p className="text-xs text-green-700 mt-2">
              Club selected. You can start scanning now.
            </p>
          )}
        </div>

        {/* Scan Mode Toggle */}
        <div className="bg-white rounded-lg p-4 mb-6 shadow-sm">
          <div className="flex space-x-2 mb-4">
            <button
              onClick={() => {
                setScanMode('camera');
                setShowCamera(true);
                resetScan();
              }}
              className={`flex-1 py-3 px-4 rounded ${scanMode === 'camera' ? 'bg-cm-gold text-cm-gold-ink' : 'bg-gray-100 text-gray-600'}`}
            >
              📷 Camera Scan
            </button>
            <button
              onClick={() => {
                setScanMode('manual');
                setShowCamera(false);
                setScannedData(null);
              }}
              className={`flex-1 py-3 px-4 rounded ${scanMode === 'manual' ? 'bg-cm-gold text-cm-gold-ink' : 'bg-gray-100 text-gray-600'}`}
            >
              ✏️ Manual Entry
            </button>
          </div>

          {/* Camera Scanner */}
          {scanMode === 'camera' && (
            <div>
              {showCamera && !scannedData ? (
                canScan ? (
                  <div className="space-y-4">
                    <div className="bg-black rounded-lg overflow-hidden" style={{ height: '300px' }}>
                      <Scanner
                        onScan={handleCameraScan}
                        onError={handleCameraError}
                        style={{
                          width: '100%',
                          height: '100%',
                        }}
                        constraints={{
                          facingMode: { ideal: 'environment' },
                          video: true
                        }}
                      />
                    </div>
                    <p className="text-center text-gray-600 text-sm">
                      Position the QR code within the camera frame
                    </p>
                    <button
                      onClick={() => setShowCamera(false)}
                      className="w-full bg-gray-500 text-white py-3 rounded hover:bg-gray-600"
                    >
                      Stop Camera
                    </button>
                  </div>
                ) : (
                  <div className="bg-yellow-50 border border-yellow-200 rounded p-3 text-sm text-yellow-800">
                    Select a club to enable camera scanning.
                  </div>
                )
              ) : !scannedData && (
                <button
                  onClick={() => setShowCamera(true)}
                  disabled={!canScan}
                  className="w-full bg-cm-gold text-cm-gold-ink py-3 rounded hover:bg-cm-gold-hover disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Start Camera
                </button>
              )}
            </div>
          )}

          {/* Manual Entry */}
          {scanMode === 'manual' && (
            <div className="space-y-4">
              <textarea
                value={scanInput}
                onChange={(e) => setScanInput(e.target.value)}
                placeholder="Paste QR code data here..."
                className="w-full p-3 border rounded h-32 resize-none"
              />
              <button
                onClick={() => handleScan()}
                disabled={loading || !scanInput.trim() || !canScan}
                className="w-full bg-cm-gold text-cm-gold-ink py-3 rounded hover:bg-cm-gold-hover disabled:opacity-50"
              >
                {loading ? 'Scanning...' : 'Scan QR Code'}
              </button>
            </div>
          )}
        </div>

        {/* Scanned Data Display */}
        {scannedData && (
          <div className="bg-white rounded-lg p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800">User Information</h3>
              <button
                onClick={resetScan}
                className="bg-cm-gold text-cm-gold-ink px-4 py-2 rounded text-sm hover:bg-cm-gold-hover"
              >
                Scan Another
              </button>
            </div>

            <div className="space-y-4">
              {/* User Basic Info */}
              <div className="bg-gray-50 p-4 rounded border">
                <div className="grid grid-cols-1 gap-3">
                  <div>
                    <span className="font-medium text-gray-600">Name:</span>
                    <span className="ml-2 text-gray-800">{scannedData.name}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-600">ID:</span>
                    <span className="ml-2 text-gray-800">{scannedData.identifier}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-600">QR Generated:</span>
                    <span className="ml-2 text-gray-800 text-sm">{scannedData.qrGeneratedAt}</span>
                  </div>
                </div>
              </div>

              {/* Verification Status */}
              <div>
                <h4 className="font-semibold text-gray-800 mb-3">Verification Status</h4>
                <div className="grid grid-cols-1 gap-3">
                  <div className={`p-3 rounded-lg border-l-4 ${scannedData.verification.aadhaar ? 'bg-green-50 border-green-500' : 'bg-red-50 border-red-500'}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-medium">Aadhaar Verification</span>
                      <span className={`px-2 py-1 rounded text-sm ${scannedData.verification.aadhaar ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {scannedData.verification.aadhaar ? '✓ Verified' : '✗ Not Verified'}
                      </span>
                    </div>
                  </div>

                  <div className={`p-3 rounded-lg border-l-4 ${scannedData.verification.pan ? 'bg-green-50 border-green-500' : 'bg-red-50 border-red-500'}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-medium">PAN Verification</span>
                      <span className={`px-2 py-1 rounded text-sm ${scannedData.verification.pan ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {scannedData.verification.pan ? '✓ Verified' : '✗ Not Verified'}
                      </span>
                    </div>
                  </div>

                  <div className={`p-3 rounded-lg border-l-4 ${scannedData.verification.bank ? 'bg-green-50 border-green-500' : 'bg-red-50 border-red-500'}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-medium">Bank Verification</span>
                      <span className={`px-2 py-1 rounded text-sm ${scannedData.verification.bank ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {scannedData.verification.bank ? '✓ Verified' : '✗ Not Verified'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Overall Status */}
              <div className={`p-4 rounded-lg text-center ${scannedData.verification.isFullyVerified ? 'bg-green-100 border border-green-300' : 'bg-yellow-100 border border-yellow-300'}`}>
                <div className={`text-2xl mb-2 ${scannedData.verification.isFullyVerified ? 'text-green-600' : 'text-yellow-600'}`}>
                  {scannedData.verification.isFullyVerified ? '✅' : '⚠️'}
                </div>
                <h4 className={`font-bold text-lg ${scannedData.verification.isFullyVerified ? 'text-green-800' : 'text-yellow-800'}`}>
                  {scannedData.verification.isFullyVerified ? 'Fully Verified User' : 'Verification Incomplete'}
                </h4>
                <p className={`text-sm mt-1 ${scannedData.verification.isFullyVerified ? 'text-green-700' : 'text-yellow-700'}`}>
                  {scannedData.verification.isFullyVerified
                    ? 'This user has completed all verification requirements'
                    : 'This user needs to complete pending verification steps'
                  }
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg text-center">
              <div className="animate-spin h-8 w-8 border-4 border-cm-gold border-t-transparent rounded-full mx-auto mb-4"></div>
              <p>Scanning QR Code...</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default QRScannerPage;
